import { useEffect, useState } from 'react'
import { setSavingsCurrent, setSavingsTarget } from '../../budget/repo'
import { publishSavings } from '../../lib/api'
import { formatMoney, parseMoney } from '../../lib/money'
import { navigate } from '../../lib/router'
import { useStore } from '../../store'
import { Bar, Card, Dialog, TopBar } from '../../ui/parts'
import { savingsView } from '../savings'

/** The same speed bump used elsewhere: it stops a stray tap, it is not a security control. */
const PUSH_PASSCODE = '4321'

export function SavingsScreen() {
  const { snapshot, mutate, showToast } = useStore()
  const [asking, setAsking] = useState(false)
  const [passcode, setPasscode] = useState('')
  const [error, setError] = useState(false)
  const [sending, setSending] = useState(false)

  if (!snapshot) return null
  const view = savingsView(snapshot.savings)

  return (
    <div className="screen">
      <TopBar title="Savings" onBack={() => navigate('/other')} />

      <div className="stack">
        <Card>
          <div className="row between">
            <span className="lbl">Saved so far</span>
            <span className="lbl">{Math.round(view.fraction * 100)}% of goal</span>
          </div>
          <div className="big">
            {formatMoney(view.currentFils)}
            <span style={{ fontSize: 15, fontWeight: 500, marginLeft: 6 }}>AED</span>
          </div>
          <Bar fraction={view.fraction} />
          <div className="muted" style={{ fontSize: 13, marginTop: 8 }}>
            {view.targetFils === 0
              ? 'Set a goal below to see how far along you are.'
              : view.reached
                ? `Goal of ${formatMoney(view.targetFils)} reached.`
                : `${formatMoney(view.remainingFils)} to go towards ${formatMoney(view.targetFils)}.`}
          </div>
          {view.updatedAt ? (
            <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
              Last updated {new Date(view.updatedAt).toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'short',
              })}
            </div>
          ) : null}
        </Card>

        <Card>
          <div className="card-title">Amounts</div>
          <MoneyField
            label="Current savings"
            fils={snapshot.savings.currentFils}
            onCommit={(fils) => void mutate((s) => setSavingsCurrent(s, fils))}
          />
          <MoneyField
            label="Goal"
            fils={snapshot.savings.targetFils}
            onCommit={(fils) => void mutate((s) => setSavingsTarget(s, fils))}
          />
        </Card>

        <Card>
          <div className="card-title">Share the balance</div>
          <div className="muted" style={{ fontSize: 13, margin: '2px 0 12px' }}>
            Sends the current amount to the other account so you both see the same number.
            Their own goal stays as it is, and nothing else of theirs is touched.
          </div>
          <button
            type="button"
            className="btn outline"
            disabled={sending}
            onClick={() => {
              setPasscode('')
              setError(false)
              setAsking(true)
            }}
          >
            {sending ? 'Sending…' : 'Push to all users'}
          </button>
        </Card>
      </div>

      {asking ? (
        <Dialog
          title="Push this amount?"
          body={
            <>
              <div style={{ marginBottom: 12 }}>
                {formatMoney(snapshot.savings.currentFils)} AED will replace the current
                savings shown on the other account.
              </div>
              <input
                type="password"
                inputMode="numeric"
                autoFocus
                placeholder="Passcode"
                value={passcode}
                className={error ? 'error' : ''}
                onChange={(e) => {
                  setPasscode(e.target.value.replace(/\D/g, '').slice(0, 8))
                  setError(false)
                }}
                style={{ width: '100%' }}
              />
              {error ? (
                <div className="over" style={{ fontSize: 13, marginTop: 6 }}>
                  Wrong passcode
                </div>
              ) : null}
            </>
          }
          confirmLabel="Push it"
          onCancel={() => setAsking(false)}
          onConfirm={async () => {
            if (passcode !== PUSH_PASSCODE) {
              setError(true)
              setPasscode('')
              return
            }
            setAsking(false)
            setSending(true)
            try {
              const updated = await publishSavings(snapshot.savings.currentFils)
              showToast(
                updated === 0
                  ? 'No other accounts to send it to'
                  : `Sent to ${updated} other account${updated === 1 ? '' : 's'}`
              )
            } catch {
              showToast('Could not send it — check your connection')
            } finally {
              setSending(false)
            }
          }}
        />
      ) : null}
    </div>
  )
}

/**
 * Committed on blur rather than per keystroke — typing "1200" would otherwise save 1, 12,
 * 120 and 1200 in a race, and the later writes come back as conflicts.
 */
function MoneyField({
  label,
  fils,
  onCommit,
}: {
  label: string
  fils: number
  onCommit: (fils: number) => void
}) {
  const [draft, setDraft] = useState(formatMoney(fils))
  useEffect(() => setDraft(formatMoney(fils)), [fils])

  return (
    <label className="field">
      <span>{label} (AED)</span>
      <input
        type="text"
        inputMode="decimal"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          const parsed = parseMoney(draft)
          if (parsed === null || parsed < 0) setDraft(formatMoney(fils))
          else if (parsed !== fils) onCommit(parsed)
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
        }}
      />
    </label>
  )
}
